import { Link } from 'react-router-dom';
import { AppsMenu } from './AppsMenu';
import { chromeFocusRing } from './MenuItem';
import { NotificationsButton } from './NotificationsButton';
import { UserMenu } from './UserMenu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { appsMenuEnabled, notificationsEnabled } from './shellFlags';

/**
 * The full-width top bar, dark in both themes. From `lg` up: the logo, a divider, the Brand Workspace
 * picker, then the account menu at the far right. Below `lg` (the tablet rail and the phone's bottom
 * bar) it is one 64px row that never wraps: the logo at the left, then the picker and the account menu
 * together at the right. New Content is never here: the sidebar, the rail or the bottom bar holds it.
 * On a tablet (`md` to `lg`) the row and the logo are the spec's exact 64px and 26px, like the rail
 * beside them, not 80% of them (the root is 12.8px there: 5rem is 64px).
 * The apps menu and the notifications bell are built but hidden (shellFlags).
 *
 * The divider lands exactly on the sidebar's right edge at every scale: the bar's 1.25rem padding + the
 * logo block + the 1rem gap puts its 1px in the last column of the sidebar's 15rem (x = 239-240 at full
 * size, 191-192 at 80%). The line itself stays 1px, so the logo block gives up exactly that: 12.75rem
 * less 1px (203px at full size). `self-stretch` and `-my-2.5` run it from the top of the bar to its
 * bottom border, so it must stay a direct child of the header.
 *
 * `z-30` from `lg` up, as before: page content stacks up to z-40 in the same root context, and the bar's
 * menus must stay above it. Below `lg` it is `z-[46]`, over the rail and the bottom bar (z-[45]), so its
 * menus' dimmed layer covers those too. Every modal (z-50) and the access lock (z-[100]) still cover it.
 */
export function Topbar() {
  return (
    <header className="relative z-30 flex h-16 shrink-0 items-center gap-x-2 border-b border-chrome-bar-line bg-chrome-bar px-4 text-chrome-text [color-scheme:dark] max-lg:z-[46] md:max-lg:h-[5rem] lg:h-auto lg:min-h-[3.5rem] lg:flex-wrap lg:gap-x-4 lg:gap-y-3 lg:px-5 lg:py-2.5">
      <div className="box-border flex flex-none items-center lg:w-[calc(12.75rem-1px)]">
        <Link to="/strategist" aria-label="Smorgas Social" className={`flex rounded ${chromeFocusRing}`}>
          <img
            src="/smorgas-social-logo.svg"
            alt=""
            width={137}
            height={21}
            className="h-[1.375rem] w-auto md:max-lg:h-[2.03125rem] lg:h-[1.3125rem] lg:w-[8.5625rem]"
          />
        </Link>
      </div>
      <div aria-hidden className="-my-2.5 hidden w-px shrink-0 self-stretch bg-chrome-bar-divider lg:block" />
      <div className="min-w-0 flex-1 lg:hidden" />
      <WorkspaceSwitcher />
      <div className="hidden flex-1 lg:block" />
      {appsMenuEnabled && <AppsMenu />}
      {notificationsEnabled && <NotificationsButton />}
      <UserMenu />
    </header>
  );
}
